import { useTranslations } from '@vocab/react';
import {
  Card,
  Column,
  Columns,
  Divider,
  Heading,
  IconEdit,
  Inline,
  Secondary,
  Stack,
  Text,
  TextLinkButton,
} from 'braid-design-system';
import React, {
  forwardRef,
  type ReactNode,
  type Ref,
  type RefObject,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { Element } from 'react-scroll';

import { editSectionLinkClicked } from 'src/app/analytics/edit';
import getTrackingAdapter from 'src/app/dataAnalytics';
import { JOB_SECTION_DISPLAYED } from 'src/app/dataAnalytics/schema/constants';
import {
  FEATURE_DATA_ANALYTICS,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import { sendLinkEvent } from 'src/app/utils/analytics';
import handleValidation from 'src/app/utils/handleValidation';
import { EditFlowDataContext } from 'src/app/views/context/cajaFlowData';
import type AppState from 'src/types/appState';
import type { SectionRef } from 'src/types/sections';
import type { TrackingEvent } from 'src/types/trackingData';

import translations from './.vocab';

interface Props {
  title: string;
  name: string;
  isOptional?: boolean;
  edit: ReactNode;
  read: ReactNode;
  fields: RefObject<any>[];
  defaultIsEdit?: boolean;
  disableEdit?: boolean;
}

const EditLabel = ({ label }: { label: string }) => (
  <Inline space="xsmall">
    <IconEdit />
    <>{label}</>
  </Inline>
);

export const EditablePageSection = forwardRef(
  (props: Props, ref: Ref<SectionRef>) => {
    const { t } = useTranslations(translations);

    const { tealium } = useSelector(
      (state: AppState) => ({ tealium: state.tealium }),
      shallowEqual,
    );

    const [hasChanges, setHasChanges] = useState(false);
    const [isEdit, setIsEdit] = useState(props.defaultIsEdit ?? false);

    const { loading } = useContext(EditFlowDataContext);
    const isPageAnalyticsEnabled = useFeatureToggleData(FEATURE_DATA_ANALYTICS);

    useEffect(() => {
      setIsEdit(props.defaultIsEdit ?? false);
    }, [props.defaultIsEdit]);

    const {
      disableEdit,
      edit: EditComponent,
      fields,
      isOptional,
      name,
      read: ReadComponent,
      title,
    } = props;

    const onValidate = () => {
      const results = handleValidation([
        {
          containerId: name,
          components: fields.filter(
            (f) => typeof f.current?.handleValidation === 'function',
          ),
        },
      ]);

      if (results.length === 0) {
        setHasChanges(true);

        return true;
      }

      return false;
    };

    const onEdit = () => {
      if (isPageAnalyticsEnabled) {
        const eventData: TrackingEvent = {
          eventName: JOB_SECTION_DISPLAYED,
        };

        getTrackingAdapter.sendLinkEvent(eventData);
      } else {
        sendLinkEvent(tealium, editSectionLinkClicked);
      }
      setIsEdit(true);
    };

    useImperativeHandle(ref, () => ({
      name,
      validate: () => onValidate(),
      edit: isEdit,
      hasChanges,
    }));

    return (
      <Element name={name} id={name}>
        <Stack space="medium">
          <Card tone={isEdit ? 'formAccent' : undefined}>
            <Stack space="large">
              <Stack space="medium">
                <Columns space="gutter" alignY="bottom">
                  <Column>
                    <Inline space="xxsmall" alignY="bottom">
                      <Heading level="3">{title}</Heading>
                      {isOptional && (
                        <Text size="standard">
                          <Secondary>
                            ({t('editable-page-section-optional-text')})
                          </Secondary>
                        </Text>
                      )}
                    </Inline>
                  </Column>
                  <Column width="content">
                    {!disableEdit && (
                      <>
                        {!isEdit && loading && (
                          <Text tone="secondary">
                            <EditLabel
                              label={t('editable-page-section-edit-text')}
                            />
                          </Text>
                        )}
                        {!isEdit && !loading && (
                          <Text>
                            <TextLinkButton
                              onClick={onEdit}
                              data={{ testid: 'edit-link' }}
                            >
                              <EditLabel
                                label={t('editable-page-section-edit-text')}
                              />
                            </TextLinkButton>
                          </Text>
                        )}
                      </>
                    )}
                  </Column>
                </Columns>
                {!isEdit && <Divider />}
              </Stack>
              {isEdit ? <>{EditComponent}</> : <>{ReadComponent}</>}
            </Stack>
          </Card>
        </Stack>
      </Element>
    );
  },
);
